import { joinRoom as joinWsRoom, selfId as wsSelfId } from "@trystero-p2p/ws-relay";
import {
  type JoinError,
  joinRoom as joinNostrRoom,
  type MessageAction,
  selfId as nostrSelfId,
  type RequestAction,
  type Room,
} from "trystero";
import type { RoomRole } from "./room-protocol.js";
import {
  decodeBoundedRoomPacket,
  decodeRoomHello,
  encodeRoomPacket,
  ROOM_APP_ID,
  type RoomHello,
} from "./room-protocol.js";

export interface TrysteroRoomOptions {
  readonly roomId: string;
  readonly hello: RoomHello;
  readonly relayUrl?: string;
  readonly handshakeTimeoutMs?: number;
}

export interface TrysteroRoomEvents {
  peerJoined(peerId: string, hello: RoomHello): void;
  peerLeft(peerId: string): void;
  joinFailed(error: Error): void;
}

export class TrysteroRoomTransport {
  readonly #room: Room;
  readonly #peerHellos = new Map<string, RoomHello>();
  #events: TrysteroRoomEvents | undefined;
  readonly selfId: string;

  constructor(options: TrysteroRoomOptions) {
    const onJoinError = (details: JoinError) =>
      this.#events?.joinFailed(
        new Error(`Could not join peer ${details.peerId}: ${details.error}`),
      );
    const callbacks = {
      handshakeTimeoutMs: options.handshakeTimeoutMs ?? 10_000,
      onJoinError,
      onPeerHandshake: async (
        peerId: string,
        send: (data: string) => Promise<void>,
        receive: () => Promise<{ data: unknown }>,
      ) => {
        const incoming = receive();
        await send(encodeRoomPacket(options.hello));
        const payload = await incoming;
        if (typeof payload.data !== "string") throw new TypeError("Invalid room handshake payload");
        const hello = decodeRoomHello(decodeBoundedRoomPacket(payload.data, 4_096));
        validateRolePair(options.hello.role, hello.role);
        this.#peerHellos.set(peerId, hello);
      },
    };
    if (options.relayUrl) {
      this.selfId = wsSelfId;
      this.#room = joinWsRoom(
        { appId: ROOM_APP_ID, relayConfig: { urls: [options.relayUrl] } },
        options.roomId,
        callbacks,
      );
    } else {
      this.selfId = nostrSelfId;
      this.#room = joinNostrRoom({ appId: ROOM_APP_ID }, options.roomId, callbacks);
    }
    this.#room.onPeerJoin = (peerId) => {
      const hello = this.#peerHellos.get(peerId);
      if (!hello) {
        this.#events?.joinFailed(new Error("Peer completed without a verified handshake"));
        return;
      }
      this.#events?.peerJoined(peerId, hello);
    };
    this.#room.onPeerLeave = (peerId) => {
      this.#peerHellos.delete(peerId);
      this.#events?.peerLeft(peerId);
    };
  }

  setEvents(events: TrysteroRoomEvents): void {
    this.#events = events;
    for (const peerId of Object.keys(this.#room.getPeers())) {
      const hello = this.#peerHellos.get(peerId);
      if (hello) events.peerJoined(peerId, hello);
    }
  }

  action(name: string): MessageAction<string> {
    return this.#room.makeAction<string>(name);
  }

  requestAction(name: string): RequestAction<string, string> {
    return this.#room.makeAction<string, string>(name, { kind: "request" });
  }

  peerConnections(): Readonly<Record<string, RTCPeerConnection>> {
    return this.#room.getPeers();
  }

  async leave(): Promise<void> {
    await this.#room.leave();
  }
}

function validateRolePair(local: RoomRole, remote: RoomRole): void {
  if (local === "host" && remote === "host") throw new TypeError("An online room has one host");
}
